import React from 'react';
import { ScanIcons } from 'src/constants/general-media';
import { AlertLevels } from 'src/models/alert-model';
import { ResolvedSideline, ResolveResult, ScanResultType, SidelineCheckResult, SidelineCheckResultType }
    from 'src/models/unload';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import UnloadScanStep, { getFailureMessage, NOT_ENABLED_MESSAGE,
    ScanResultMessage } from 'src/components/pages/unload-page/common/UnloadScanStep';

// A Record, so an outcome without a message fails to compile; an accepted sideline ID opens the next step instead
const MESSAGE_BY_RESULT_TYPE: Record<SidelineCheckResultType,
    (scan: SidelineCheckResult) => ScanResultMessage | undefined> = {
    [ScanResultType.ACCEPTED]: () => undefined,
    [ScanResultType.ALREADY_RESOLVED]: scan => ({ type: AlertLevels.INFORMATIONAL,
        title: AppStrings.UNLOAD_ALREADY_RESOLVED_TITLE,
        lines: [[scan.value, scan.resolvedContainerId].join(AppStrings.UNLOAD_CHAIN_SEPARATOR)] }),
    [ScanResultType.NOT_LINKED]: scan => ({ type: AlertLevels.ERROR, title: scan.value,
        lines: [AppStrings.UNLOAD_SIDELINE_NOT_LINKED] }),
    [ScanResultType.NOT_ENABLED]: () => NOT_ENABLED_MESSAGE,
    [ScanResultType.REFUSED]: getFailureMessage,
    [ScanResultType.SYSTEM_ERROR]: getFailureMessage
};

interface ScanSidelineToResolvePageProps {
    // The latest sideline ID scanned here that opened no next step, with its result
    lastSidelineCheck?: SidelineCheckResult;
    // The sideline ID just resolved, and the label it was resolved to
    resolved?: ResolvedSideline;
    // Takes each sideline ID
    onSubmit: (sidelineId: string) => void;
}

// Resolve Sideline's first step: a sideline ID on a current trailer or pallet opens the next step
export const ScanSidelineToResolvePage = ({ lastSidelineCheck, resolved, onSubmit }: ScanSidelineToResolvePageProps) => (
    <UnloadScanStep icon={ScanIcons.SCAN_HANDHELD_ICON} heading={AppStrings.UNLOAD_RESOLVE_SIDELINE_HEADING}
                    fieldTestId="unload-resolve-sideline-scan-field"
                    fieldLabel={AppStrings.UNLOAD_SIDELINE_FIELD_LABEL}
                    message={lastSidelineCheck ? MESSAGE_BY_RESULT_TYPE[lastSidelineCheck.outcome](lastSidelineCheck)
                        : resolved && { type: AlertLevels.SUCCESS, title: AppStrings.UNLOAD_RESOLVED_TITLE,
                            lines: [[resolved.sidelineId, resolved.containerId].join(AppStrings.UNLOAD_CHAIN_SEPARATOR)] }}
                    onSubmit={onSubmit}/>
);

interface ScanOwnLabelPageProps {
    // The sideline ID being resolved
    sidelineId: string;
    // What the screens call the pallet or trailer it is on, when the service named one
    parentName?: string;
    // A resolve not done, with its result
    lastResolve?: ResolveResult;
    // Takes each label
    onSubmit: (label: string) => void;
}

// Its second step: the label of the container the sideline ID stands for resolves it. The sideline ID is named over
// the pallet or trailer it is on
export const ScanOwnLabelPage = ({ sidelineId, parentName, lastResolve, onSubmit }: ScanOwnLabelPageProps) => (
    <UnloadScanStep icon={ScanIcons.SCAN_HANDHELD_ICON} fieldTestId="unload-own-label-scan-field"
                    heading={[sidelineId, parentName].filter(Boolean).join(AppStrings.UNLOAD_SEPARATOR)}
                    fieldLabel={AppStrings.UNLOAD_CONTAINER_FIELD_LABEL}
                    placeholder={AppStrings.UNLOAD_CONTAINER_FIELD_PLACEHOLDER}
                    prompt={AppStrings.UNLOAD_OWN_LABEL_IDLE_PROMPT}
                    message={lastResolve && getFailureMessage(lastResolve)}
                    onSubmit={onSubmit}/>
);
